import {
  MAX_CHARS_IN_BIO,
  MAX_CHARS_IN_POPULATION,
  MIN_CHARS_IN_BIO,
  MIN_CHARS_IN_POPULATION,
} from '@/constants/numberVars';
import { EMAIL_REGEX } from '@/constants/stringVars';
import {
  APPOINTMENT_TYPES,
  AVAILABILITIES,
  DESIGNATIONS,
  MODALITIES,
  SCHEDULE_TOOLS,
  SPECIALTIES,
} from '@/types/Provider.types';
import * as yup from 'yup';

const validationSchema = yup.object().shape(
  {
    first_name: yup
      .string()
      .trim()
      .required('First Name is required.')
      .min(2, 'First name needs to be at least 2 characters long.'),
    last_name: yup
      .string()
      .trim()
      .required('Last Name is required.')
      .min(2, 'Last name needs to be at least 2 characters long.'),
    email: yup
      .string()
      .notRequired()
      .trim()
      .when('email', {
        is: (val: string) => val?.length > 0,
        then: () =>
          yup
            .string()
            .min(2, 'Email needs to be at least 2 characters long.')
            .matches(EMAIL_REGEX, 'Wrong email format.'),
        otherwise: () => yup.string().notRequired(),
      }),
    practice_name: yup
      .string()
      .notRequired()
      .trim()
      .when('practice_name', {
        is: (val: string) => val?.length > 0,
        then: () => yup.string().min(2, 'Practice name needs to be at least 2 characters long.'),
        otherwise: () => yup.string().notRequired(),
      }),
    designation: yup.array().of(yup.mixed().oneOf(DESIGNATIONS)).required('Designations are required.'),
    bio: yup
      .string()
      .trim()
      .when('bio', {
        is: (val: string) => val?.length > 0,
        then: () =>
          yup
            .string()
            .min(MIN_CHARS_IN_BIO, `Minimum ${MIN_CHARS_IN_BIO} characters are required in the About Me`)
            .max(MAX_CHARS_IN_BIO, `Maximum ${MAX_CHARS_IN_BIO} characters allowed in the About Me`),
        otherwise: () => yup.string().notRequired(),
      }),
    schedule_tools: yup.array().of(yup.mixed().oneOf(SCHEDULE_TOOLS)),
    ehr_software: yup
      .string()
      .notRequired()
      .trim()
      .when('ehr_software', {
        is: (val: string) => val?.length > 0,
        then: () => yup.string().min(2, 'EHR software name needs to be at least 2 characters long.'),
        otherwise: () => yup.string().notRequired(),
      }),
    sync_schedule_interest: yup.boolean(), // .required('Sync schedule interest is required.')
    appointment_types: yup.array().of(yup.mixed().oneOf(APPOINTMENT_TYPES)), // .required('Appointment types are required.')
    specialities: yup.array().of(yup.mixed().oneOf(SPECIALTIES)),
    availability: yup.string().oneOf(AVAILABILITIES),
    modalities: yup.array().of(yup.mixed().oneOf(MODALITIES)),
    booking_link: yup.string().when('booking_link', {
      is: (val: string) => val?.length > 0,
      then: () => yup.string().url('Invalid URL format').max(100, 'Maximum 100 characters allowed in the booking link'),
      otherwise: () => yup.string().notRequired(),
    }),
    custom_designation: yup.string().notRequired().trim(),
    custom_schedule_tool: yup.string().notRequired().trim(),
    custom_appointment_type: yup.string().notRequired().trim(),
    custom_speciality: yup.string().notRequired().trim(),
    population: yup
      .string()
      .trim()
      .when('population', {
        is: (val: string) => val?.length > 0,
        then: () =>
          yup
            .string()
            .min(
              MIN_CHARS_IN_POPULATION,
              `Minimum ${MIN_CHARS_IN_POPULATION} characters are required in the Population`,
            )
            .max(MAX_CHARS_IN_POPULATION, `Maximum ${MAX_CHARS_IN_POPULATION} characters allowed in the Population`),
        otherwise: () => yup.string().notRequired(),
      }),
  },
  [
    ['email', 'email'],
    ['ehr_software', 'ehr_software'],
    ['practice_name', 'practice_name'],
    ['bio', 'bio'],
    ['population', 'population'],
    ['booking_link', 'booking_link'],
  ],
);

export { validationSchema as providerValidationSchema };
